import { useState } from 'react'
import { useAuth } from 'ict24h-themes-react'
import {
  SearchQueryProjectMemberInput,
  ProjectMemberEntity,
  Paging,
} from '../../../../../../../server-api-hrm.type'
import commonRequestHandler from '../../../../../../../utils/common-request-handler'
import { MetadataType } from '../../../../../../../utils/metadata.types'
import requestErrorHandler from '../../../../../../../utils/request-error-handler'
import uniqueArrayById from '../../../../../../../utils/unique-array-by-id'
import {
  getListProjectMember,
  getDetailProjectMember,
} from '../project-member.service'
import { setProjectMembers } from '../../../stores/project-reducer'

type LoadType = 'fetch' | 'load-more' | 're-fetch'
type RequestFailType = 'SERVER' | 'RESOURCE' | 'OTHER'

type Params = {
  projectId: string | undefined | null
  filter?: SearchQueryProjectMemberInput | null
  onGetListFinish?: (_data: ProjectMemberEntity[]) => void
  onGetListFail?: (_failType: RequestFailType) => void
  onGetDetailFail?: (_failType: RequestFailType) => void
  autoGetFullData?: boolean
  customRequestHandler?: boolean
}

type GetDataParams = {
  type: LoadType
  paging?: Paging
  localHandler?: Omit<Params, 'onGetDetailFail'>
}

type GetDetailParams = {
  id?: string
  localHandler?: Pick<Params, 'customRequestHandler' | 'onGetDetailFail'>
}

const useGetDataProjectMember = (params: Params = { projectId: undefined }) => {
  const { token } = useAuth()

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<ProjectMemberEntity[]>([])
  const [detail, setDetail] = useState<ProjectMemberEntity>()
  const [metadata, setMetadata] = useState<MetadataType>()

  const handleResetData = () => {
    setData([])
    setMetadata(undefined)
  }

  const getData = async ({
    type,
    paging,
    localHandler = { projectId: undefined },
  }: GetDataParams) => {
    const projectId = localHandler.projectId || params.projectId
    const filter = { ...params.filter, ...localHandler.filter }
    const onFinish = localHandler.onGetListFinish || params.onGetListFinish
    const onFail = localHandler.onGetListFail || params.onGetListFail
    const autoGetFullData =
      localHandler.autoGetFullData ?? params.autoGetFullData
    const customRequestHandler =
      localHandler?.customRequestHandler ?? params.customRequestHandler

    const requestHandler = customRequestHandler
      ? requestErrorHandler
      : commonRequestHandler

    if (type === 'fetch') handleResetData()
    setLoading(true)
    const res = await getListProjectMember(token, {
      paging: { pageLimit: autoGetFullData ? 100 : undefined, ...paging },
      filter,
      projectId: projectId || filter.projectId,
    })
    setLoading(false)

    if (!res) {
      if (typeof onFail === 'function') onFail('OTHER')
      return
    }

    // Lợi dụng setState để lấy data vừa update mới nhất
    const finishFlow = () =>
      setData((prev) => {
        if (typeof onFinish === 'function') onFinish(prev)
        // return lại data gốc
        return prev
      })

    requestHandler({
      status: res?.status,
      onSuccess: () => {
        setMetadata(res.data?.metadata)
        setData((prev) =>
          type === 'load-more'
            ? uniqueArrayById([...prev, ...(res.data?.data || [])])
            : res.data?.data || []
        )
        setProjectMembers(res.data?.data || [], type)

        if (autoGetFullData) {
          const nextPageId = res?.data?.metadata?.paging?.cursor?.next
          if (nextPageId) {
            getData({
              type: 'load-more',
              paging: { pageLimit: 100, pageId: nextPageId },
              localHandler,
            })
          } else {
            finishFlow()
          }
        } else {
          finishFlow()
        }
      },
      onResourceFail: () => {
        if (typeof onFail === 'function') onFail('RESOURCE')
      },
      onServerFail: () => {
        if (typeof onFail === 'function') onFail('SERVER')
      },
    })
  }

  const getDetail = async ({ id, localHandler = {} }: GetDetailParams) => {
    if (!id) return
    const onFail = localHandler.onGetDetailFail || params.onGetDetailFail
    const customRequestHandler =
      localHandler?.customRequestHandler ?? params.customRequestHandler

    const requestHandler = customRequestHandler
      ? requestErrorHandler
      : commonRequestHandler

    setLoading(true)
    const res = await getDetailProjectMember(token, { id })
    setLoading(false)

    if (!res) {
      if (typeof onFail === 'function') onFail('OTHER')
      return
    }
    requestHandler({
      status: res?.status,
      onSuccess: () => setDetail(res.data),
      onResourceFail: () => {
        if (typeof onFail === 'function') onFail('RESOURCE')
      },
      onServerFail: () => {
        if (typeof onFail === 'function') onFail('SERVER')
      },
    })
  }

  return {
    loading,
    data,
    detail,
    metadata,
    getDetail,
    getData,
  }
}

export default useGetDataProjectMember
